import { Map } from 'immutable';

import { getRetainFromVariations } from './retain';
import { updateVariationsErrors } from '../validations/variations';
import { updateCombinations } from './combinations';
import { getOptionFormatter } from '../taxonomy';
import { deleteVariant } from './variants';
import { setInvalid } from './errors';
import { getSize } from '../iterable/getSize';

import { COMBINATIONS, NUMBER_OF_VARIANTS, INFLUENCE, VARIANTS, ALL, RETAIN } from '../../constants/variations';
import { AMOUNT, ATTRIBUTE_DEFAULTS, ERROR, VALUE, WEIGHT_UNITS } from '../../constants/product';
import { CUSTOM_PROPERTY_IDS } from '../../constants/taxonomy';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { EMPTY, VELA } from '../../constants';
import ATTRIBUTE from '../../constants/attributes';

function convertEtsyVariationsToShopify({ inventory, validate = true, variations: source }) {
  let variations = source;
  const emptyValue = inventory ? '' : null;
  const variants = variations.get(VARIANTS);

  function reduceVariants(result, variant, variantIndex) {
    result[variantIndex] = getOptionFormatter({ channel: ETSY, scale: variant.get('scale') });
    return result;
  }

  const formatters = variants.reduce(reduceVariants, []);
  const trackQuantity = getSize(variations.getIn([INFLUENCE, ATTRIBUTE.QUANTITY])) > 0;

  function mapVariants(variant, variantIndex) {
    function mapOptions(option, optionIndex) {
      return option
        .delete('valueId')
        .set('id', -1 - optionIndex)
        .set('value', formatters[variantIndex](option.get('value')));
    }

    return variant
      .delete('property')
      .delete('scale')
      .set('id', -1 - variantIndex)
      .set('options', variant.get('options').map(mapOptions));
  }

  function mapCombinations(combination) {
    function mapOptions(option, optionIndex) {
      return option
        .delete('optionId')
        .delete('variantId')
        .set('name', formatters[optionIndex](option.get('name')));
    }

    return combination
      .delete(ATTRIBUTE.VISIBILITY)
      .set('id', '')
      .set('options', combination.get('options').map(mapOptions))
      .set(ATTRIBUTE.BARCODE, emptyValue)
      .set(ATTRIBUTE.CAP, emptyValue)
      .set(ATTRIBUTE.COUNTRY_CODE, emptyValue)
      .set(ATTRIBUTE.CPI, emptyValue)
      .set(ATTRIBUTE.HS_CODE, emptyValue)
      .set(ATTRIBUTE.PHYSICAL, true)
      .set(ATTRIBUTE.PRICE, combination.get(ATTRIBUTE.PRICE))
      .set(ATTRIBUTE.QUANTITY, combination.get(ATTRIBUTE.QUANTITY))
      .set(ATTRIBUTE.SKU, combination.get(ATTRIBUTE.SKU))
      .set(ATTRIBUTE.TRACK_QUANTITY, trackQuantity)
      .set(ATTRIBUTE.UNIT, WEIGHT_UNITS[0])
      .set(ATTRIBUTE.WEIGHT, emptyValue);
  }

  variations = variations
    .delete(ERROR)
    .set(INFLUENCE, Map({ [ATTRIBUTE.TRACK_QUANTITY]: trackQuantity }))
    .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations))
    .set(VARIANTS, variants.map(mapVariants));

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: SHOPIFY, variations }));
  }

  return validate
    ? setInvalid(updateVariationsErrors({ channel: SHOPIFY, variations }))
    : variations;
}

function convertShopifyVariationsToEtsy({ inventory, validate = true, variations: source }) {
  const emptyValue = inventory ? '' : null;
  let variations = source;

  function reduceCombinations(result, combination) {
    result[ATTRIBUTE.PRICE] = result[ATTRIBUTE.PRICE] || combination.get(ATTRIBUTE.PRICE) !== emptyValue;
    result[ATTRIBUTE.QUANTITY] = result[ATTRIBUTE.QUANTITY] || combination.get(ATTRIBUTE.TRACK_QUANTITY);
    result[ATTRIBUTE.SKU] = result[ATTRIBUTE.SKU] || combination.get(ATTRIBUTE.SKU) !== emptyValue;
    return result;
  }

  const influences = variations.get(COMBINATIONS).reduce(reduceCombinations, {});

  function reduceVariants(result, variant, variantIndex) {
    if (variantIndex >= NUMBER_OF_VARIANTS[ETSY]) return result;

    function mapOptions(option, optionIndex) {
      return option
        .set('id', -1 - optionIndex)
        .set('valueId', null);
    }

    result.influence = result.influence.set(variantIndex, true);
    result.variants = result.variants.push(
      variant
        .set('id', -1 - variantIndex)
        .set('property', CUSTOM_PROPERTY_IDS[ETSY][variantIndex])
        .set('options', variant.get('options').map(mapOptions))
    );

    return result;
  }

  const { influence, variants } = variations
    .get(VARIANTS)
    .slice(0, 2)
    .reduce(reduceVariants, { influence: EMPTY.MAP, variants: EMPTY.LIST });

  function mapCombinations(combination) {
    function mapOptions(option, optionIndex) {
      function findOption(variationOption) {
        return variationOption.get('value') === option.get('name');
      }

      const variantId = variants.getIn([optionIndex, 'id']);
      const optionId = variants.getIn([optionIndex, 'options']).find(findOption).get('id');

      return option
        .set('optionId', optionId)
        .set('variantId', variantId);
    }

    const price = influences[ATTRIBUTE.PRICE]
      ? combination.get(ATTRIBUTE.PRICE)
      : inventory
        ? inventory.getIn([ATTRIBUTE.PRICE, VALUE], emptyValue)
        : emptyValue;

    const quantity = influences[ATTRIBUTE.QUANTITY]
      ? combination.get(ATTRIBUTE.QUANTITY)
      : inventory
        ? inventory.getIn([ATTRIBUTE.QUANTITY, VALUE], emptyValue)
        : emptyValue;

    const sku = influences[ATTRIBUTE.SKU]
      ? combination.get(ATTRIBUTE.SKU)
      : inventory
        ? inventory.getIn([ATTRIBUTE.SKU, VALUE], emptyValue)
        : emptyValue;

    return Map({
      id: '',
      options: combination.get('options').slice(0, 2).map(mapOptions),
      [ATTRIBUTE.PRICE]: price,
      [ATTRIBUTE.QUANTITY]: quantity,
      [ATTRIBUTE.SKU]: sku,
      [ATTRIBUTE.VISIBILITY]: true,
    });
  }

  variations = variations
    .set(INFLUENCE,
      Map({
        [ATTRIBUTE.PRICE]: influences[ATTRIBUTE.PRICE] ? influence : EMPTY.MAP,
        [ATTRIBUTE.QUANTITY]: influences[ATTRIBUTE.QUANTITY] ? influence : EMPTY.MAP,
        [ATTRIBUTE.SKU]: influences[ATTRIBUTE.SKU] ? influence : EMPTY.MAP,
      })
    )
    .set(VARIANTS, variants)
    .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations));

  variations = variations.set(COMBINATIONS,
    updateCombinations({ channel: ETSY, inventory, variants, variations })
  );

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: ETSY, variations }));
  }

  return validate
    ? setInvalid(updateVariationsErrors({ channel: ETSY, variations }))
    : variations;
}

function convertVelaVariationsToEtsy({ category, inventory, validate = true, variations: source }) {
  let variations = source;

  while (getSize(variations.get(VARIANTS)) > NUMBER_OF_VARIANTS[ETSY]) {
    variations = deleteVariant({
      category,
      channel: VELA,
      inventory,
      variantIndex: getSize(variations.get(VARIANTS)) - 1,
      variations,
    });
  }

  function reduceVariants(result, variant, variantIndex) {
    return result.set(variantIndex, true);
  }

  function updateVariantsForEtsy(variants = EMPTY.LIST) {
    function mapVariants(variant = EMPTY.MAP, variantIndex) {
      function updateOptions(options = EMPTY.LIST) {
        function mapOptions(option = EMPTY.MAP) {
          return option
            .set('valueId', null)
            .set('imageHash', null);
        }

        return options.map(mapOptions);
      }

      return variant
        .set('property', CUSTOM_PROPERTY_IDS[ETSY][variantIndex])
        .update('options', updateOptions);
    }

    return variants.map(mapVariants);
  }

  function updateCombinationsForEtsy(combinations = EMPTY.LIST) {
    function mapCombinations(combination = EMPTY.MAP) {
      return combination.set(ATTRIBUTE.VISIBILITY, true);
    }

    return combinations.map(mapCombinations);
  }

  const allVariants = variations.get(VARIANTS).reduce(reduceVariants, EMPTY.MAP);

  const influence = Map({
    [ALL]: true,
    [ATTRIBUTE.PRICE]: allVariants,
    [ATTRIBUTE.QUANTITY]: allVariants,
    [ATTRIBUTE.SKU]: allVariants,
  });

  variations = variations
    .set(INFLUENCE, influence)
    .update(VARIANTS, updateVariantsForEtsy)
    .update(COMBINATIONS, updateCombinationsForEtsy);

  return validate
    ? setInvalid(updateVariationsErrors({ channel: ETSY, variations }))
    : variations;
}

function convertVelaVariationsToShopify({ inventory, validate = true, variations: source }) {
  let variations = source;

  function updateVariantsForShopify(variants = EMPTY.LIST) {
    function mapVariants(variant = EMPTY.MAP) {
      function updateOptions(options = EMPTY.LIST) {
        function mapOptions(option = EMPTY.MAP) {
          return option.delete('id');
        }

        return options.map(mapOptions);
      }

      return variant.update('options', updateOptions);
    }

    return variants.map(mapVariants);
  }

  function updateCombinationsForShopify(combinations = EMPTY.LIST) {
    function mapCombinations(combination = EMPTY.MAP) {
      function updateOptions(options = EMPTY.LIST) {
        function mapOptions(option = EMPTY.MAP) {
          return option.deleteAll(['optionId', 'variantId']);
        }

        return options.map(mapOptions);
      }

      const chargeTax = inventory.getIn(
        [ATTRIBUTE.CHARGE_TAX, VALUE],
        ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CHARGE_TAX]
      );

      const continueSelling = inventory.getIn(
        [ATTRIBUTE.CONTINUE_SELLING, VALUE],
        ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CONTINUE_SELLING]
      );

      return combination
        .set(ATTRIBUTE.BARCODE, '')
        .set(ATTRIBUTE.CAP, AMOUNT.ZERO_CENT)
        .set(ATTRIBUTE.CHARGE_TAX, chargeTax)
        .set(ATTRIBUTE.CONTINUE_SELLING, continueSelling)
        .set(ATTRIBUTE.COUNTRY_CODE, null)
        .set(ATTRIBUTE.CPI, AMOUNT.ZERO_CENT)
        .set(ATTRIBUTE.HS_CODE, null)
        .set(ATTRIBUTE.PHYSICAL, true)
        .set(ATTRIBUTE.TRACK_QUANTITY, true)
        .set(ATTRIBUTE.UNIT, WEIGHT_UNITS[0])
        .set(ATTRIBUTE.WEIGHT, AMOUNT.ZERO)
        .set('imageId', null)
        .set('imageHash', null)
        .update('options', updateOptions);
    }

    return combinations.map(mapCombinations);
  }

  variations = variations
    .set(INFLUENCE, Map({ [ATTRIBUTE.TRACK_QUANTITY]: true }))
    .update(VARIANTS, updateVariantsForShopify)
    .update(COMBINATIONS, updateCombinationsForShopify);

  return validate
    ? setInvalid(updateVariationsErrors({ channel: SHOPIFY, variations }))
    : variations;
}

export function convertVariations({ from, to, variations, ...rest }) {
  switch (from) {
    case ETSY: {
      switch (to) {
        case SHOPIFY: {
          return convertEtsyVariationsToShopify({ variations, ...rest });
        }

        default: {
          return variations;
        }
      }
    }

    case SHOPIFY: {
      switch (to) {
        case ETSY: {
          return convertShopifyVariationsToEtsy({ variations, ...rest });
        }

        default: {
          return variations;
        }
      }
    }

    case VELA: {
      switch (to) {
        case ETSY: {
          return convertVelaVariationsToEtsy({ variations, ...rest });
        }

        case SHOPIFY: {
          return convertVelaVariationsToShopify({ variations, ...rest });
        }

        default: {
          return variations;
        }
      }
    }

    default:
      return variations;
  }
}

